import React, { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { ExpandLess, ExpandMore, Settings as SettingsIcon, People, CalendarToday, Business, Build, Description, Receipt, Assignment, ListAlt, ChevronLeft, ChevronRight } from '@mui/icons-material';
import styled from "styled-components";
import dashboardSvg from "../../shared/icons/sidebarMenu/dashboard.svg";
import callCenterSvg from "../../shared/icons/sidebarMenu/callCenter.svg";
import clientsSvg from "../../shared/icons/sidebarMenu/clients.svg";
import employeesSvg from "../../shared/icons/sidebarMenu/employees.svg";
import appointmentsSvg from "../../shared/icons/sidebarMenu/appointments.svg";
import payrollSvg from "../../shared/icons/sidebarMenu/payroll.svg";
import meetingSvg from "../../shared/icons/sidebarMenu/meeting.svg";
import referralSvg from "../../shared/icons/sidebarMenu/referral.svg";
import historySvg from "../../shared/icons/sidebarMenu/history.svg";
import helpSvg from "../../shared/icons/sidebarMenu/help.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { useGetUserInfoQuery, useGetRolePermissionsQuery } from "../../services/ClientService";
import { useTabNavigation } from "../../hooks/useTabNavigation";

export const SidebarMenuTitle = styled.div`
  color: #333;
  font-family: Open Sans;
  font-size: 13px;
  font-weight: 500;
  margin-left: 16px;
  white-space: nowrap;
  /* Shrinkable + clipped so the collapsed rail (which leaves this label zero room) clips it to
     nothing rather than letting it overflow and push on the icon. */
  min-width: 0;
  overflow: hidden;
  /* Always mounted so it can fade rather than pop; the container's overflow:hidden clips it while
     collapsed. Opacity leads the width so the label is gone before the rail finishes shrinking. */
  opacity: ${props => (props.$show ? 1 : 0)};
  pointer-events: ${props => (props.$show ? 'auto' : 'none')};
  transition: opacity 0.18s ease-in-out;
`;

const SidebarMenuContainer = styled.div`
  background: #fff;
  top: 60px;
  height: 100%;
  z-index: 999;
  position: fixed;
  height: calc(100% - 60px);
  width: ${props => props.$isExpanded ? '220px' : '60px'};
  box-shadow: 2px 0 8px rgba(0,0,0,0.07);
  border-right: 1px solid #eee;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarMenuItem = styled(ListItem)`
  && {
    align-items: center;
    display: flex;
    height: 44px;
    /* Constant left padding in both states. With the 24px icon column, 18 + 24 + 18 = 60px, so the
       icon lands dead-centre of the collapsed rail AND keeps the exact same x when expanded — no
       horizontal jump as the width animates. */
    padding: 0 18px;
    justify-content: flex-start;
    cursor: pointer;
    background: ${props => (props.active ? "#f5f5f5" : "transparent")};
    &:hover {
      background: #f0e9e0;
    }
    text-decoration: none;
  }
`;

/* Trailing area of the Settings row (spacer + expand chevron): fades with the labels so it doesn't
   pop when the rail is collapsed. */
const TrailingFade = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  opacity: ${props => (props.$show ? 1 : 0)};
  pointer-events: ${props => (props.$show ? 'auto' : 'none')};
  transition: opacity 0.18s ease-in-out;
`;

export const ImgContainer = styled.div`
  /* Fixed basis, no grow/shrink: the always-mounted label is a flex sibling, and when collapsed it
     overflows and would otherwise squeeze this icon a pixel or two (but not when expanded), which
     read as a slight jump. Locking the basis keeps the icon at exactly the same x in both states. */
  flex: 0 0 24px;
  width: 24px;
  display: flex;
  justify-content: center;

  img {
    filter: invert(41%) sepia(92%) saturate(747%) hue-rotate(183deg) brightness(93%) contrast(92%);
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

export const SidebarMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { openPage } = useTabNavigation();

  // Sidebar gating based on Module permissions
  // Provided module IDs:
  // 1 Billing Tools (Billing)
  // 3 Authorization Tools (Authorization)
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const skipPermissions = !token;

  const { data: userInfo } = useGetUserInfoQuery(undefined, { skip: skipPermissions });
  const currentRoleId = Number(userInfo?.roleId ?? userInfo?.RoleId ?? 0);
  const currentUserId = String(userInfo?.userId ?? userInfo?.UserId ?? userInfo?.id ?? userInfo?.Id ?? '');
  const shouldEnforceRolePermissions = Boolean(currentRoleId);

  const { data: rolePermissionsRaw = [], isLoading: isRolePermissionsLoading } = useGetRolePermissionsQuery(
    { roleId: currentRoleId, userId: currentUserId },
    { skip: skipPermissions || !shouldEnforceRolePermissions || !currentUserId }
  );

    const isUserManagementAdmin = Number(
    userInfo?.userManagementRoleId ?? userInfo?.UserManagementRoleId ?? 0
  ) === 1;

  const rolePermissions = React.useMemo(() => {
    const arr = Array.isArray(rolePermissionsRaw) ? rolePermissionsRaw : [];
    return arr.map((p) => ({
      permissionTypeId: Number(p?.permissionTypeId ?? p?.PermissionTypeId ?? 0),
      linkId: String(p?.linkId ?? p?.LinkId ?? ''),
      accessLevelId: Number(p?.permissionAccessLevelId ?? p?.PermissionAccessLevelId ?? 0),
    }));
  }, [rolePermissionsRaw]);

  // Debug: log permissions once loaded (avoids console spam)
  useEffect(() => {
    if (skipPermissions) return;
    if (!shouldEnforceRolePermissions) return;
    if (isRolePermissionsLoading) return;

    const getMaxAccessForModule = (moduleId) => {
      const moduleIdStr = String(moduleId);
      return rolePermissions
        .filter((p) => p.permissionTypeId === 1 && p.linkId === moduleIdStr)
        .reduce((acc, p) => Math.max(acc, Number(p.accessLevelId || 0)), 0);
    };

    const settingsIds = [9, 10, 11, 12, 13, 14, 15, 16];
    const settingsAccess = settingsIds.map((id) => {
      const maxAccess = getMaxAccessForModule(id);
      return {
        moduleId: id,
        maxAccess,
        canView: maxAccess >= 1,
        canEdit: maxAccess >= 2,
      };
    });

    console.groupCollapsed('[Sidebar RBAC] Current user permissions');
    console.log('roleId:', currentRoleId);
    console.log('raw permissions:', rolePermissionsRaw);
    console.log('normalized permissions:', rolePermissions);
    console.log('settings permissions (module 9-16):');
    try {
      console.table(settingsAccess);
    } catch {
      console.log(settingsAccess);
    }
    console.groupEnd();
  }, [
    skipPermissions,
    shouldEnforceRolePermissions,
    isRolePermissionsLoading,
    currentRoleId,
    rolePermissionsRaw,
    rolePermissions,
  ]);

  const getModuleAccess = React.useCallback((moduleId) => {
    if (!shouldEnforceRolePermissions || isUserManagementAdmin) return { canView: true, canEdit: true };
    if (isRolePermissionsLoading) return { canView: false, canEdit: false };

    const moduleIdStr = String(moduleId);
    const maxAccess = rolePermissions
      .filter((p) => p.permissionTypeId === 1 && p.linkId === moduleIdStr)
      .reduce((acc, p) => Math.max(acc, Number(p.accessLevelId || 0)), 0);

    return {
      canView: maxAccess >= 1,
      canEdit: maxAccess >= 2,
    };
  }, [shouldEnforceRolePermissions, isRolePermissionsLoading, rolePermissions, isUserManagementAdmin]);

  const canViewBillingTools = getModuleAccess(1).canView;
  const canViewAuthorizationTools = getModuleAccess(3).canView;

  // Settings module permissions
  // 9 Users
  // 10 User Schedules
  // 11 Role Permissions
  // 12 Providers
  // 13 Services
  // 14 ERA
  // 15 Forms
  // 16 Test
  const canViewUsersSettings = getModuleAccess(9).canView;
  const canViewUserSchedulesSettings = getModuleAccess(10).canView;
  const canViewRolePermissionsSettings = getModuleAccess(11).canView;
  const canViewProvidersSettings = getModuleAccess(12).canView;
  const canViewServicesSettings = getModuleAccess(13).canView;
  const canViewERASettings = getModuleAccess(14).canView;
  const canViewFormsSettings = getModuleAccess(15).canView;
  const canViewTestSettings = getModuleAccess(16).canView;

  const anySettingsVisible =
    canViewUsersSettings ||
    canViewUserSchedulesSettings ||
    canViewRolePermissionsSettings ||
    canViewProvidersSettings ||
    canViewServicesSettings ||
    canViewERASettings ||
    canViewFormsSettings ||
    canViewTestSettings;

  // State for collapsible menus
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Sidebar collapse state: pinned=true → always expanded; false → icons only until hover
  const [pinned, setPinned] = useState(true);
  const [hovered, setHovered] = useState(false);
  const isExpanded = pinned || hovered;

  // Keep the CSS custom-property in sync so PageLayout can respond to the width change.
  React.useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isExpanded ? '220px' : '60px');
  }, [isExpanded]);

  // Gates the Settings sub-menu's own fade-in until the rail's width transition has actually
  // finished expanding — so the two animations run in sequence (rail widens, then sub-items fade
  // in) instead of racing each other. Collapsing is the opposite: this flips to false in the same
  // tick as isExpanded, so the sub-menu's Collapse (below) closes instantly, matching the rail.
  const [railFullyExpanded, setRailFullyExpanded] = React.useState(isExpanded);
  React.useEffect(() => {
    if (!isExpanded) {
      setRailFullyExpanded(false);
      return;
    }
    const t = setTimeout(() => setRailFullyExpanded(true), 220); // matches the rail's own width transition
    return () => clearTimeout(t);
  }, [isExpanded]);

  // Set the initial value synchronously on first render.
  React.useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', '220px');
  }, []);

  const handleMenuItemClick = (path, title) => {
    openPage(path, title);
  };

  const handleSettingsToggle = () => {
    setSettingsOpen((prev) => !prev);
  };

  // Only show sidebar if user is authenticated
  if (!accounts || accounts.length === 0) return null;

  return (
    <SidebarMenuContainer
      $isExpanded={isExpanded}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <AuthenticatedTemplate>
        {/* Collapse / pin toggle */}
        {/* Constant justify/padding so the toggle glides with the animating right edge instead of
            snapping when the expand state flips (justify-content can't tween). */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          borderBottom: '1px solid #f0e9e0',
          py: 0.25,
          px: 0.5,
        }}>
          <Tooltip title={pinned ? 'Collapse sidebar' : 'Pin sidebar open'} placement="right">
            <IconButton size="small" onClick={() => setPinned(p => !p)}>
              {pinned ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

         <List component="nav" disablePadding>
          <Tooltip title={isExpanded ? '' : 'Dashboard'} placement="right">
            <SidebarMenuItem button active={location.pathname.includes("dashboard")} onClick={() => handleMenuItemClick('/dashboard', 'Dashboard')}>
              <ImgContainer>
                <img src={dashboardSvg} alt="" />
              </ImgContainer>
              <SidebarMenuTitle $show={isExpanded}>Dashboard</SidebarMenuTitle>
            </SidebarMenuItem>
          </Tooltip>

          <Tooltip title={isExpanded ? '' : 'Clients'} placement="right">
            <SidebarMenuItem button active={location.pathname === "/home" || location.pathname.includes("client")} onClick={() => handleMenuItemClick('/home', 'Clients')}>
              <ImgContainer>
                <img src={clientsSvg} alt="" />
              </ImgContainer>
              <SidebarMenuTitle $show={isExpanded}>Clients</SidebarMenuTitle>
            </SidebarMenuItem>
          </Tooltip>

          {canViewBillingTools && (
            <Tooltip title={isExpanded ? '' : 'Billing'} placement="right">
              <SidebarMenuItem button active={location.pathname.includes("bilingReport")} onClick={() => handleMenuItemClick('/bilingReport', 'Billing')}>
                <ImgContainer>
                  <img src={payrollSvg} alt="" />
                </ImgContainer>
                <SidebarMenuTitle $show={isExpanded}>Billing</SidebarMenuTitle>
              </SidebarMenuItem>
            </Tooltip>
          )}

          {canViewAuthorizationTools && (
            <Tooltip title={isExpanded ? '' : 'Authorization'} placement="right">
              <SidebarMenuItem button active={location.pathname.includes("authorizationReport")} onClick={() => handleMenuItemClick('/authorizationReport', 'Authorization')}>
                <ImgContainer>
                  <img src={referralSvg} alt="" />
                </ImgContainer>
                <SidebarMenuTitle $show={isExpanded}>Authorization</SidebarMenuTitle>
              </SidebarMenuItem>
            </Tooltip>
          )}

          <Tooltip title={isExpanded ? '' : 'Calendar'} placement="right">
            <SidebarMenuItem button active={location.pathname.includes("calendar")} onClick={() => handleMenuItemClick('/calendar', 'Calendar')}>
              <ImgContainer>
                <img src={appointmentsSvg} alt="" />
              </ImgContainer>
              <SidebarMenuTitle $show={isExpanded}>Calendar</SidebarMenuTitle>
            </SidebarMenuItem>
          </Tooltip>

            {anySettingsVisible && (
            <>
              <Divider sx={{ my: 1 }} />

              {/* Settings — collapsible when expanded, single icon when collapsed */}
              <Tooltip title={isExpanded ? '' : 'Settings'} placement="right">
                <SidebarMenuItem button onClick={() => {
                  if (!isExpanded) { setPinned(true); setSettingsOpen(true); }
                  else handleSettingsToggle();
                }}>
                  <ImgContainer>
                    <SettingsIcon style={{ color: '#846a4a' }} />
                  </ImgContainer>
                  <SidebarMenuTitle $show={isExpanded}>Settings</SidebarMenuTitle>
                  <TrailingFade $show={isExpanded}>
                    <Box sx={{ flex: 1 }} />
                    {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                  </TrailingFade>
                </SidebarMenuItem>
              </Tooltip>

              {/* Gated on railFullyExpanded (not raw isExpanded) so opening Settings while the rail
                  is still collapsed doesn't fade the sub-items in DURING the rail's own width
                  transition — the fade only starts once the rail has actually finished expanding,
                  running the two animations in sequence instead of racing. Collapsing stays instant
                  (timeout 0): railFullyExpanded flips false in the same tick as isExpanded, so the
                  sub-items disappear immediately alongside the rail, not animated. */}
              <Collapse
                in={settingsOpen && railFullyExpanded}
                timeout={isExpanded ? 220 : 0}
                easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {canViewUsersSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/users', 'Users')}>
                      <ListItemIcon><People fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Users" />
                    </SidebarMenuItem>
                  )}

                  {canViewUserSchedulesSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/employeeCalendar', 'Employee Calendar')}>
                      <ListItemIcon><CalendarToday fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Employee Calendar" />
                    </SidebarMenuItem>
                  )}

                  {canViewRolePermissionsSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/role-permissions', 'Role Permissions')}>
                      <ListItemIcon><ListAlt fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Role Permissions" />
                    </SidebarMenuItem>
                  )}

                  {canViewProvidersSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/providers', 'Providers')}>
                      <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Providers" />
                    </SidebarMenuItem>
                  )}

                  {canViewServicesSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/services', 'Services')} active={location.pathname.startsWith('/services')}>
                      <ListItemIcon><Build fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Services" />
                    </SidebarMenuItem>
                  )}

                  {canViewERASettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/era', 'ERA')}>
                      <ListItemIcon><Receipt fontSize="small" /></ListItemIcon>
                      <ListItemText primary="ERA" />
                    </SidebarMenuItem>
                  )}

                  {canViewFormsSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/forms', 'Forms')}>
                      <ListItemIcon><Description fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Forms" />
                    </SidebarMenuItem>
                  )}

                  {isUserManagementAdmin && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/list-management', 'List Management')} active={location.pathname.startsWith('/list-management')}>
                      <ListItemIcon><ListAlt fontSize="small" /></ListItemIcon>
                      <ListItemText primary="List Management" />
                    </SidebarMenuItem>
                  )}

                  {canViewTestSettings && (
                    <SidebarMenuItem button sx={{ pl: 6 }} onClick={() => handleMenuItemClick('/test', 'Test')}>
                      <ListItemIcon><Assignment fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Test" />
                    </SidebarMenuItem>
                  )}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </AuthenticatedTemplate>
    </SidebarMenuContainer>
  );
};